// Shared accessible modal dialog.
//
// Rendered through a portal into document.body, so an ancestor with
// `backdrop-filter`/`transform` (the sticky topbar has one) can never become the
// containing block of the fixed backdrop and clip the dialog.
//
// Handles: role="dialog" + aria-modal, labelling via title/description, Escape
// (only the topmost open modal reacts), focus trap, initial focus, focus
// restore on close, backdrop click and body scroll lock.
import { Children, cloneElement, isValidElement, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const openStack = [];
let scrollLocks = 0;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {React.ReactNode} [props.title]        rendered as <h2> in .modal-h, labels the dialog
 * @param {React.ReactNode} [props.description]  rendered under the title, describes the dialog
 * @param {React.ReactNode} [props.icon]         optional icon block before the title
 * @param {React.ReactNode} [props.footer]       rendered in .modal-f
 * @param {React.ReactNode} props.children       body; wrap in <div className="modal-body"> for padding
 * @param {'div'|'form'} [props.as]
 * @param {(e: Event) => void} [props.onSubmit]  when as="form"
 * @param {number|string} [props.maxWidth]
 * @param {string} [props.className]             extra classes on the dialog (e.g. "modal--danger")
 * @param {string} [props.dialogClassName]       replaces the default "modal" class
 * @param {string} [props.backdropClassName]     replaces the default "modal-bg" class
 * @param {string} [props.ariaLabel]             when there is no visible title
 * @param {React.RefObject} [props.initialFocusRef]
 * @param {React.RefObject} [props.returnFocusRef]  where focus goes on close when the
 *   element that opened the dialog is gone (e.g. an item in a closed menu)
 * @param {boolean} [props.closeOnBackdrop]      default true
 * @param {boolean} [props.closeOnEscape]        default true
 */
export default function Modal({
  onClose,
  title,
  description,
  icon,
  footer,
  children,
  as = 'div',
  onSubmit,
  maxWidth,
  className = '',
  dialogClassName = 'modal',
  backdropClassName = 'modal-bg',
  ariaLabel,
  initialFocusRef,
  returnFocusRef,
  closeOnBackdrop = true,
  closeOnEscape = true,
}) {
  const dialogRef = useRef(null);
  const downOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descId = useId();
  const token = useRef({});

  useEffect(() => {
    const me = token.current;
    openStack.push(me);
    const previouslyFocused = document.activeElement;

    scrollLocks += 1;
    if (scrollLocks === 1) document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const target =
      initialFocusRef?.current ||
      dialog?.querySelector('[autofocus], [data-autofocus]') ||
      dialog?.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])') ||
      dialog;
    target?.focus({ preventScroll: true });

    const onKey = (e) => {
      if (openStack[openStack.length - 1] !== me) return;
      if (e.key === 'Escape' && closeOnEscape) {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = focusables(dialogRef.current);
      if (items.length === 0) {
        e.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    // Focus that escapes the topmost dialog (e.g. a click on the backdrop) is pulled back.
    const onFocusIn = (e) => {
      if (openStack[openStack.length - 1] !== me) return;
      if (dialogRef.current && !dialogRef.current.contains(e.target)) dialogRef.current.focus();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocusIn);
      const i = openStack.indexOf(me);
      if (i !== -1) openStack.splice(i, 1);
      scrollLocks = Math.max(0, scrollLocks - 1);
      if (scrollLocks === 0) document.body.style.overflow = '';
      const back =
        previouslyFocused && previouslyFocused.isConnected && previouslyFocused !== document.body
          ? previouslyFocused
          : returnFocusRef?.current;
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
    };
    // Mount/unmount only: re-running would steal focus on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Tag = as;
  // min(..., 100%) keeps a per-dialog max width from overriding the
  // stylesheet's max-width: 100% on narrow screens.
  const style =
    maxWidth != null
      ? { maxWidth: `min(${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth}, 100%)` }
      : undefined;

  return createPortal(
    <div
      className={backdropClassName}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Only a press that both starts and ends on the backdrop closes; a text
        // selection dragged out of an input must not.
        if (closeOnBackdrop && downOnBackdrop.current && e.target === e.currentTarget) onClose?.();
        downOnBackdrop.current = false;
      }}
    >
      <Tag
        ref={dialogRef}
        className={`${dialogClassName} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        style={style}
        onSubmit={as === 'form' ? onSubmit : undefined}
      >
        {(title || icon) && (
          <div className="modal-h">
            {icon}
            {title && <h2 id={titleId}>{title}</h2>}
            {description && <p id={descId}>{description}</p>}
          </div>
        )}
        {children}
        {footer && <div className="modal-f">{footer}</div>}
      </Tag>
    </div>,
    document.body,
  );
}

/**
 * Label + control + optional hint/error, with the label bound to the control
 * (htmlFor/id) and the hint/error bound via aria-describedby.
 * The single child element is the control; it gets an id if it has none.
 */
export function Field({ label, hint, error, children, className }) {
  const autoId = useId();
  const child = Children.only(children);
  const id = (isValidElement(child) && child.props.id) || autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [child.props['aria-describedby'], hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={className}>
      {label && <label htmlFor={id}>{label}</label>}
      {cloneElement(child, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : child.props['aria-invalid'],
      })}
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
